import React, { useMemo, useRef, useState } from 'react';
import { Loader2, Save, X, AlertTriangle, UserCheck } from 'lucide-react';
import { SifaMitarbeiter } from '../../services/sifa/types';
import { MitarbeiterEingabe, upsertMitarbeiter } from '../../services/sifa/sifaService';
import { findeMitarbeiter } from '../../services/sifa/mitarbeiterMatch';
import { describeError } from '../../services/errorMessage';
import { formatDatum } from './unterweisungModulLogic';

interface Props {
  projectId: string;
  /** null = neu anlegen. */
  mitarbeiter: SifaMitarbeiter | null;
  /** Der ganze Stamm — fuer die Dubletten-Vorschau beim Tippen. */
  bestand: SifaMitarbeiter[];
  isReadOnly?: boolean;
  onClose: () => void;
  onGespeichert: (m: SifaMitarbeiter) => void;
  /** „Diesen verwenden": den Dialog auf die gefundene Person umschalten. */
  onWechseln: (m: SifaMitarbeiter) => void;
}

/**
 * Anlegen/Bearbeiten einer Person.
 *
 * Beim Tippen laeuft die Regel „3 von 4" (findeMitarbeiter, TS-Vorschau der
 * SQL-Regel) gegen den geladenen Stamm. Trifft sie, steht ueber dem
 * Speichern-Knopf „Möglicherweise schon vorhanden" mit „Diesen verwenden" —
 * so entsteht die Dublette gar nicht erst, statt spaeter zusammengefuehrt
 * werden zu muessen. Die SQL-Fassung entscheidet beim Abschluss einer
 * Teilnahme; hier ist es nur die Vorschau.
 */
export const MitarbeiterDialog: React.FC<Props> = ({ projectId, mitarbeiter, bestand, isReadOnly = false, onClose, onGespeichert, onWechseln }) => {
  const [vorname, setVorname] = useState(mitarbeiter?.vorname || '');
  const [nachname, setNachname] = useState(mitarbeiter?.nachname || '');
  const [geburtsdatum, setGeburtsdatum] = useState(mitarbeiter?.geburtsdatum || '');
  const [email, setEmail] = useState(mitarbeiter?.email || '');
  const [abteilung, setAbteilung] = useState(mitarbeiter?.abteilung || '');
  const [personalnummer, setPersonalnummer] = useState(mitarbeiter?.personalnummer || '');
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const speichertRef = useRef(false);

  const dublette = useMemo(() => {
    if (!vorname.trim() || !nachname.trim()) return null;
    const andere = bestand.filter((m) => m.id !== mitarbeiter?.id);
    const e = findeMitarbeiter({ vorname, nachname, geburtsdatum: geburtsdatum || null, email: email || null }, andere);
    return e.treffer || e.kandidaten[0] || null;
  }, [vorname, nachname, geburtsdatum, email, bestand, mitarbeiter?.id]);

  const speichern = async () => {
    if (isReadOnly || speichertRef.current) return;
    if (!vorname.trim() || !nachname.trim()) { setFehler('Vorname und Nachname sind Pflicht.'); return; }
    speichertRef.current = true;
    setSpeichert(true);
    setFehler(null);
    try {
      const eingabe: MitarbeiterEingabe = {
        id: mitarbeiter?.id,
        vorname: vorname.trim(),
        nachname: nachname.trim(),
        geburtsdatum: geburtsdatum || null,
        email: email.trim() || null,
        abteilung: abteilung.trim() || null,
        personalnummer: personalnummer.trim() || null,
      };
      const gespeichert = await upsertMitarbeiter(projectId, eingabe);
      onGespeichert(gespeichert);
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      speichertRef.current = false;
      setSpeichert(false);
    }
  };

  const feld = 'w-full px-3 py-2 min-h-[40px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-cyan-300 disabled:text-slate-500';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">{mitarbeiter ? (isReadOnly ? 'Mitarbeiter' : 'Mitarbeiter bearbeiten') : 'Mitarbeiter anlegen'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full"><X className="w-4 h-4 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Vorname</label>
              <input value={vorname} onChange={(e) => setVorname(e.target.value)} disabled={isReadOnly} className={feld} autoFocus />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Nachname</label>
              <input value={nachname} onChange={(e) => setNachname(e.target.value)} disabled={isReadOnly} className={feld} />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Geburtsdatum</label>
              <input type="date" value={geburtsdatum} onChange={(e) => setGeburtsdatum(e.target.value)} disabled={isReadOnly} className={feld} />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Personalnummer</label>
              <input value={personalnummer} onChange={(e) => setPersonalnummer(e.target.value)} disabled={isReadOnly} className={feld} />
            </div>
            <div className="col-span-2">
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">E-Mail</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isReadOnly} className={feld} />
            </div>
            <div className="col-span-2">
              <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Abteilung</label>
              <input value={abteilung} onChange={(e) => setAbteilung(e.target.value)} disabled={isReadOnly} className={feld} />
            </div>
          </div>

          {dublette && !isReadOnly && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              <div className="flex-1">
                <p className="font-bold">Möglicherweise schon vorhanden:</p>
                <p>{dublette.vorname} {dublette.nachname}{dublette.geburtsdatum ? `, ${formatDatum(dublette.geburtsdatum)}` : ''}{dublette.email ? `, ${dublette.email}` : ''}{dublette.abteilung ? ` (${dublette.abteilung})` : ''}</p>
                {!mitarbeiter && (
                  <button onClick={() => onWechseln(dublette)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-amber-900 underline">
                    <UserCheck className="w-3.5 h-3.5" /> Diesen verwenden
                  </button>
                )}
              </div>
            </div>
          )}

          {fehler && <p className="text-xs text-red-700">{fehler}</p>}
        </div>
        {!isReadOnly && (
          <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
            <button onClick={onClose} className="min-h-[40px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl">Abbrechen</button>
            <button onClick={() => void speichern()} disabled={speichert} className="min-h-[40px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-700 rounded-xl disabled:opacity-40">
              {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
